import React, { useState, useEffect } from "react";
import {
    Flex, Icon, Image, Text, useColorModeValue, Link, Button, IconButton,
    Tooltip, useClipboard, Box, Checkbox, HStack, VStack, Badge,
    Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody
} from "@chakra-ui/react";
// Custom components
import Card from "components/card/Card.js";
import RelevanceBadge from "components/badge/RelevanceBadge";
// Assets
import { StarIcon } from "@chakra-ui/icons";
import { MdCheckCircle, MdShoppingCart, MdContentCopy, MdOutlineError, MdOutlineReportOff, MdStore, MdCalendarToday, MdFactCheck, MdPlaylistAddCheck, MdComment } from "react-icons/md";
import CommentPopover from "./CommentPopover";
import { IoMdAlert } from "react-icons/io";
import Title from "services/Title";
import Amazon from 'assets/img/amazon.ico';
import Flipkart from 'assets/img/flipkart.ico';
import Snapdeal from 'assets/img/snapdeal.ico';
import Meesho from 'assets/img/meesho.ico';
import Glowroad from 'assets/img/glowroad.ico';
import Shopsy from 'assets/img/shopsy.ico';

// --- Internal Helper Components ---

// 1. Overlay Component (Adapted for Item functionality: Report & Test)
const StatusOverlay = ({ report, test }) => {
    if (!report && !test) return null;

    const config = report
        ? { icon: MdOutlineError, color: "gold", label: "REPORTED" }
        : { icon: MdShoppingCart, color: "blue.500", label: "TEST BUY" };

    return (
        <Box
            position="absolute"
            top="0" left="0" right="0" bottom="0"
            bg="blackAlpha.700"
            borderRadius="xl"
            zIndex="2"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            transition="all 0.2s"
        >
            <Icon as={config.icon} color={config.color} w={10} h={10} mb={2} />
            <Text color="white" fontWeight="bold" fontSize="xs" letterSpacing="wider">
                {config.label}
            </Text>
        </Box>
    );
};

const getIcon = (iconTag, isActive, size = 20) => {
    const inactiveGray = "#A0AEC0"
    return (
        <Icon
            as={iconTag}
            color={isActive ? "white" : inactiveGray}
            boxSize={`${size}px`} // This increases the actual icon size
        />

    )
};

// --- Formats and displays the detection date; click the calendar icon to reveal
// report/takedown dates (only the ones present on the product are shown) ---
const formatDate = (d) => {
    if (!d) return null;
    const parsed = new Date(d);
    if (isNaN(parsed.getTime())) return d; // fall back to raw value if unparsable
    return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const DetectedDateBadge = ({ date, dateReported, dateTakendown, variant = "grid" }) => {
    if (!date) return null;

    const extraRows = [
        dateReported ? { label: "Reported", value: formatDate(dateReported) } : null,
        dateTakendown ? { label: "Takedown", value: formatDate(dateTakendown) } : null,
    ].filter(Boolean);

    const popoverContent = (
        <PopoverContent w="auto" minW="150px">
            <PopoverArrow />
            <PopoverBody py={2} px={3}>
                <VStack align="start" spacing={1}>
                    {/* <Text fontSize="xs" color="gray.500">Detected: <b>{formatDate(date)}</b></Text> */}
                    {extraRows.map((row) => (
                        <Text key={row.label} fontSize="xs" color="gray.500">{row.label}: <b>{row.value}</b></Text>
                    ))}
                </VStack>
            </PopoverBody>
        </PopoverContent>
    );

    if (variant === "list") {
        return (
            <Popover trigger="click" placement="top" isLazy>
                <HStack spacing={1} p="1">
                    <PopoverTrigger>
                        <IconButton
                            aria-label="Show status dates"
                            icon={<Icon as={MdCalendarToday} color="gray.400" boxSize="11px" />}
                            size="xs"
                            variant="ghost"
                            minW="auto"
                            h="auto"
                        //   p="1"
                        />
                    </PopoverTrigger>
                    <Text>Detected: <Text as="span" fontWeight="semibold" color="inherit">{formatDate(date)}</Text></Text>
                </HStack>
                {popoverContent}
            </Popover>
        );
    }

    return (
        <Popover trigger="click" placement="top" isLazy>
            <PopoverTrigger>
                <Badge
                    as="button"
                    type="button"
                    colorScheme="purple"
                    variant="subtle"
                    fontSize="9px"
                    px={2}
                    py={1}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    w="fit-content"
                    cursor="pointer"
                >
                    <Icon as={MdCalendarToday} boxSize="10px" />
                    Detected: {formatDate(date)}
                </Badge>
            </PopoverTrigger>
            {popoverContent}
        </Popover>
    );
};

// 2. Action Buttons (Specific to Item: Report & Test)
const BottomActions = ({ isAdmin, handlers, toggles, borderColor, mode }) => {
    if (!isAdmin || mode === 'nonCompliance') return null;

    const { toggleReport, toggleTest } = toggles;
    const { onToggleReport, onToggleTest } = handlers;

    return (
        <Box px={3} py={2} borderTop="1px solid" borderColor={borderColor}>
            <HStack spacing={2} width="100%">
                <Tooltip label='Move to Takedown'>
                    <Button
                        size="sm"
                        flex={1}
                        leftIcon={<Icon as={MdOutlineError} />}
                        colorScheme={toggleReport ? "red" : "red"}
                        variant={toggleReport ? "solid" : "outline"}
                        onClick={onToggleReport}
                    >
                        Takedown
                    </Button>
                </Tooltip>

                <Tooltip label='Test Purchase'>
                    <Button
                        size="sm"
                        flex={1}
                        leftIcon={<Icon as={MdShoppingCart} />}
                        colorScheme={toggleTest ? "blue" : "gray"}
                        variant={toggleTest ? "solid" : "solid"}
                        onClick={onToggleTest}
                    >
                        Test Buy
                    </Button>
                </Tooltip>
            </HStack>
        </Box>
    );
};

const TopRightActions = ({ isAdmin, handlers, toggles, mode,viewMode, compactActions = false, product, onSaveComments }) => {

    const size = compactActions ? "sm" : "md";
    const { toggleReport, toggleTest, toggleNonCompliance, toggleWhiteList, toggleReported, toggleNonReportable } = toggles;
    const { onToggleReport, onToggleTest, onToggleNonCompliance, onToggleWhiteList, onToggleReported, onToggleNonReportable } = handlers;

    return (
        <VStack spacing={2} position={viewMode === 'grid' ? "absolute" : "relative"} top="10px" right="10px" zIndex="3" align="flex-end">
            {isAdmin && <>
                {compactActions && (
                    <>
                        <Tooltip label='Takedown'>
                            <IconButton
                                size={size}
                                variant={toggleReport ? "solid" : "ghost"}
                                colorScheme={toggleReport ? "red" : "gray"}
                                aria-label="Takedown"
                                icon={getIcon(MdOutlineError, toggleReport, compactActions ? 14 : 20)}
                                onClick={onToggleReport}
                            />
                        </Tooltip>

                        <Tooltip label='Test Purchase'>
                            <IconButton
                                size={size}
                                variant={toggleTest ? "solid" : "ghost"}
                                colorScheme={toggleTest ? "blue" : "gray"}
                                aria-label="Test purchase"
                                icon={getIcon(MdShoppingCart, toggleTest, compactActions ? 14 : 20)}
                                onClick={onToggleTest}
                            />
                        </Tooltip>
                    </>
                )}


                {mode === 'nonCompliance' ? (
                    <Tooltip label='Move to Reported'>
                        <IconButton
                            size={size}
                            variant={toggleReported ? "solid" : "ghost"}
                            colorScheme={toggleReported ? "orange" : "gray"}
                            aria-label="Move to reported"
                            icon={getIcon(MdOutlineError, toggleReported, compactActions ? 14 : 20)}
                            onClick={onToggleReported}
                        />
                    </Tooltip>
                ) : (
                    <Tooltip label='Move to Non Compliance'>
                        <IconButton
                            size={size}
                            variant={toggleNonCompliance ? "solid" : "ghost"}
                            colorScheme={toggleNonCompliance ? "purple" : "gray"}
                            aria-label="Move to non compliance"
                            icon={getIcon(MdFactCheck, toggleNonCompliance, compactActions ? 14 : 20)}
                            onClick={onToggleNonCompliance}
                        />
                    </Tooltip>
                )}

                <Tooltip label='Move to Non-Reportable'>
                    <IconButton
                        size={size}
                        variant={toggleNonReportable ? "solid" : "ghost"}
                        colorScheme={toggleNonReportable ? "orange" : "gray"}
                        aria-label="Move to non-reportable"
                        icon={getIcon(MdOutlineReportOff, toggleNonReportable, compactActions ? 14 : 20)}
                        onClick={onToggleNonReportable}
                    />
                </Tooltip>

                {isAdmin && (
                    <Tooltip label='Whitelist Seller'>
                        <IconButton
                            size={size}
                            variant={toggleWhiteList ? "solid" : "ghost"}
                            colorScheme={toggleWhiteList ? "green" : "gray"}
                            aria-label="Whitelist seller"
                            icon={getIcon(MdCheckCircle, toggleWhiteList, compactActions ? 14 : 20)}
                            onClick={onToggleWhiteList}
                        />
                    </Tooltip>
                )}
            </>
            }

            <CommentPopover product={product} isAdmin={isAdmin} onSave={onSaveComments} size={size} compact={compactActions} />
        </VStack>
    );
};

// --- GridView Design (Matches SuspiciousItem Grid) ---
const GridView = ({ product, index, isAdmin, handlers, toggles, common, currentRelevance,viewMode,
    handleRelevanceChange, mode }) => {
    const { textColor, borderColor, getPlatformIcon, renderStars, onCopy, hasCopied, toolTipMessage, chooseAbuseType } = common;
    const { toggleReport, toggleTest, toggleNonCompliance, toggleWhiteList, toggleReported, toggleNonReportable } = toggles;

    // Determine active border color based on state
    const activeColor = toggleReport ? "orange.400" : toggleTest ? "blue.400" : toggleNonCompliance ? "purple.400" : toggleWhiteList ? "green.400" : toggleReported ? "orange.500" : toggleNonReportable ? "purple.700" : borderColor;
    const isSelected = toggleReport || toggleTest || toggleNonCompliance || toggleWhiteList || toggleReported || toggleNonReportable;

    return (
        <Card
            p='5px'
            bg={useColorModeValue("white", "navy.800")}
            transition='0.3s cubic-bezier(.25,.8,.25,1)'
            _hover={{ shadow: "lg", transform: "translateY(-4px)" }}
            border={isSelected ? "2px solid" : "1px solid"}
            borderColor={isSelected ? activeColor : borderColor}
            overflow='hidden' h="100%" display="flex" flexDirection="column" position="relative"
        >
            {/* Image Section */}
            <Box position='relative' bg='white' p='15px' h='200px' borderBottom="1px solid" borderColor={borderColor} borderRadius="xl" overflow="hidden">
                <TopRightActions isAdmin={isAdmin} viewMode={viewMode} handlers={handlers} toggles={toggles} mode={mode} product={product} onSaveComments={handlers.onSaveComments} />
                <StatusOverlay report={toggleReport} test={toggleTest} />

                <Link href={product.image_link} isExternal w='100%' h='100%' display="block">
                    <Image src={product.image_link} w='100%' h='100%' objectFit='contain' />
                </Link>

                {/* Platform Icon Box */}
                <Box position='absolute' top='10px' left='10px' bg="white" borderRadius="md" p="1" shadow="sm" border="1px solid" borderColor="gray.100">
                    <Image src={getPlatformIcon(product.platform_id)} w='18px' h='18px' />
                </Box>

                {/* Global Status Icon (Verified/Alert) */}
                <Flex position='absolute' top='10px' right='10px' alignItems={"center"}>
                    {product.global_status && (
                        //  <Box position='absolute' top='10px' right='10px'>
                        <Tooltip label={toolTipMessage(product.global_status)}>
                            <Box bg="white" borderRadius="full" p={1} shadow="sm">
                                <Icon
                                    as={(product.global_status === 20 || product.global_status === 21) ? IoMdAlert : MdCheckCircle}
                                    color={(product.global_status === 20 || product.global_status === 21) ? "red.500" : "green.500"}
                                    w={5} h={5}
                                />
                            </Box>
                        </Tooltip>
                        //  </Box>
                    )}

                    {product.abuse_type_id && (
                        <Badge colorScheme="red" variant="subtle" fontSize="0.7em">
                            {chooseAbuseType(product.abuse_type_id)}
                        </Badge>
                    )}
                </Flex>
            </Box>

            {/* Content Section */}
            <VStack px='15px' align='start' spacing='8px' flex='1' py={2}>
                <Link href={product.prod_link} isExternal w="100%">
                    <Text color={textColor} fontWeight='700' fontSize='sm' noOfLines={2} h="40px">
                        {index + 1}. <Title product={product} />
                        {/* {product.title} */}
                    </Text>
                </Link>
                <HStack w="100%" justify="space-between">
                    {!product.seller_name?.toLowerCase().startsWith("xxxx") && (
                        <HStack spacing="1px">
                            {renderStars(product.overall_rating)}
                        </HStack>
                    )}

                    <Box ml="auto">
                        <RelevanceBadge
                            value={currentRelevance}
                            editable="true"
                            onChange={handleRelevanceChange}
                        />
                    </Box>
                </HStack>

                <HStack w="100%" justify="space-between">
                    {/* <Badge variant="subtle" colorScheme="brand" fontSize="10px">
                          {product.keyword || "General"}
                        </Badge> */}
                    <Text color={textColor} fontWeight='900' fontSize='xl' hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>₹{product.selling_price < 0 ? "N/A" : product.selling_price}</Text>
                    <DetectedDateBadge date={product.date_detected} dateReported={product.date_report} dateTakendown={product.date_takendown} variant="grid" />

                </HStack>
                {/* Gray Info Box */}
                <VStack w='100%' align="start" spacing={1} bg={"gray.50"} p="2" borderRadius="md">
                    <HStack w="100%" justify="space-between">
                        <Text fontSize='13px' color="gray.500" noOfLines={1} hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
                            Seller: <b>{product.seller_name}</b>
                        </Text>
                    </HStack>

                    {product.store_name && (
                        <HStack fontSize='13px' color="gray.500">
                            <Icon as={MdStore} />
                            <Text noOfLines={1}>{product.store_name}</Text>
                        </HStack>
                    )}

                    <HStack w="100%" justify="space-between" pt={1} borderTop="1px dashed" borderColor="gray.200">
                        <Text fontSize='13px' color="gray.500">Code: <b>{product.prod_code}</b></Text>
                        <IconButton size='xs' icon={<MdContentCopy />} variant='ghost' h="20px" aria-label="Copy" onClick={onCopy} />
                    </HStack>
                </VStack>
            </VStack>

            <BottomActions isAdmin={isAdmin} handlers={handlers} toggles={toggles} borderColor={borderColor} mode={mode} />
        </Card>
    );
};

// --- ListView Design (Matches SuspiciousItem List) ---
const ListView = ({ product, index, isAdmin, handlers, toggles, common, currentRelevance,
    handleRelevanceChange, mode,viewMode, borderColor }) => {
    const { textColor, renderStars, getPlatformIcon, onCopy, toolTipMessage, hasCopied, chooseAbuseType } = common;
    const { toggleReport, toggleTest, toggleNonCompliance, toggleWhiteList, toggleReported, toggleNonReportable } = toggles;

    const getBorderColor = () => {
        if (toggleReport) return "orange.400";
        if (toggleTest) return "blue.400";
        if (toggleNonCompliance) return "purple.400";
        if (toggleWhiteList) return "green.400";
        if (toggleReported) return "orange.500";
        if (toggleNonReportable) return "gray.500";
        return "transparent";
    };

    const getBgColor = () => {
        if (toggleReport) return { bg: "orange.50", _dark: { bg: "rgba(237, 137, 54, 0.1)" } };
        if (toggleTest) return { bg: "blue.50", _dark: { bg: "rgba(66, 153, 225, 0.1)" } };
        if (toggleNonCompliance) return { bg: "purple.50", _dark: { bg: "rgba(128, 90, 213, 0.1)" } };
        if (toggleWhiteList) return { bg: "green.50", _dark: { bg: "rgba(72, 187, 120, 0.1)" } };
        if (toggleReported) return { bg: "orange.50", _dark: { bg: "rgba(237, 137, 54, 0.1)" } };
        if (toggleNonReportable) return { bg: "purple.70", _dark: { bg: "rgba(142, 96, 239, 0.1)" } };
        return { bg: "white", _dark: { bg: "navy.800" } };
    };

    const bgStyles = getBgColor();
    return (
        <Card
            p="16px"
            mb="10px"
            position="relative"
            overflow="hidden"
            borderLeftWidth={toggleReport || toggleTest ? "5px" : "0px"}
            borderLeftColor={getBorderColor()}
            transition="all 0.2s"
            bg={bgStyles.bg}
            _dark={bgStyles._dark}
            boxShadow="sm"
        // _hover={{ boxShadow: "md" }}
        >
            <Flex align="center" gap={4}>

                {/* 1. Image Section */}
                <Box position="relative" w="100px" h="100px" flexShrink={0}>
                    <Link href={product.image_link} isExternal w='100%' h='100%' display="block">
                        <Image
                            src={product.image_link}
                            w="100%"
                            h="100%"
                            objectFit="contain"
                            fallbackSrc="https://via.placeholder.com/100?text=No+Image"
                        />
                    </Link>
                    <Box
                        position='absolute' bottom='0' left='0'
                        bg="white" _dark={{ bg: "navy.900" }}
                        p="1" shadow="sm"
                    >
                        <Image src={getPlatformIcon(product.platform_id)} w='16px' />
                    </Box>
                </Box>

                {/* 2. Details Section */}
                <Box flex="1">
                    {/* Title Row */}
                    <Flex align="center" mb={1} wrap="wrap">
                        <Text
                            fontWeight="bold"
                            fontSize="md"
                            color="brands.900"
                            _dark={{ color: "white" }}
                            mr={2}
                        >
                            {index + 1}.&nbsp;
                            <Link href={product.prod_link} isExternal textDecoration="none" _hover={{ textDecoration: "underline" }}>
                                {product.title}
                            </Link>
                        </Text>

                        {/* Status Badges */}
                        {toggleReport && <Badge colorScheme="orange" variant="solid" mr={2}>TAKEDOWN</Badge>}
                        {toggleTest && <Badge colorScheme="blue" variant="solid" mr={2}>TEST BUY</Badge>}
                        {toggleNonCompliance && <Badge colorScheme="purple" variant="solid" mr={2}>NC</Badge>}
                        {toggleNonReportable && <Badge colorScheme="purple" variant="solid" mr={2}>Non Reportable</Badge>}
                        {toggleWhiteList && <Badge colorScheme="green" variant="solid" mr={2}>WHITELIST</Badge>}
                        {toggleReported && <Badge colorScheme="orange" variant="solid" mr={2}>REPORTED</Badge>}

                        {/* Global Status Icon */}
                        {product.global_status && (
                            <Tooltip label={toolTipMessage(product.global_status)}>
                                <Box as="span" display="flex" alignItems="center">
                                    <Icon
                                        as={(product.global_status === 20 || product.global_status === 21) ? IoMdAlert : MdCheckCircle}
                                        color={(product.global_status === 20 || product.global_status === 21) ? "red.500" : "green.500"}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                    </Flex>

                    {/* Keyword & Rating Row */}
                    <HStack spacing={4} mb={2}>
                        <Text fontSize="sm" color="secondaryGray.600" _dark={{ color: "gray.400" }}>
                            {product.keyword}
                        </Text>
                        <HStack spacing="1px" hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>{renderStars(product.overall_rating)}</HStack>
                        {product.abuse_type_id && (
                            <Badge colorScheme="red" variant="subtle" fontSize="0.7em">
                                {chooseAbuseType(product.abuse_type_id)}
                            </Badge>)}
                        <DetectedDateBadge date={product.date_detected} dateReported={product.date_report} dateTakendown={product.date_takendown} variant="grid" />
                        <RelevanceBadge
                            value={currentRelevance}
                            editable="true"
                            onChange={handleRelevanceChange}
                        />

                    </HStack>

                    {/* Price & Info Row */}
                    <Flex gap={6} align="center" fontSize="sm" wrap="wrap">
                        <Text hidden={product.seller_name?.toLowerCase().startsWith("xxxx")} fontWeight="bold" color="brands.900" _dark={{ color: "white" }}>
                            ₹{product.selling_price}
                        </Text>
                        <Text hidden={product.seller_name?.toLowerCase().startsWith("xxxx")} color="secondaryGray.600" textDecoration="line-through">
                            ₹{product.mrp}
                        </Text>

                        <HStack>
                            <Text color="secondaryGray.500">Code: {product.prod_code}</Text>
                            <Tooltip label={hasCopied ? "Copied!" : "Copy Code"}>
                                <IconButton
                                    size='xs'
                                    icon={<MdContentCopy />}
                                    onClick={onCopy}
                                    variant='ghost'
                                    aria-label="copy"
                                    colorScheme={hasCopied ? "green" : "gray"}
                                />
                            </Tooltip>
                        </HStack>

                        <Text hidden={product.seller_name?.toLowerCase().startsWith("xxxx")} color="secondaryGray.600">
                            Seller: <Text as="span" fontWeight="semibold" color="brands.900" _dark={{ color: "white" }}>{product.seller_name}</Text>
                        </Text>
                    </Flex>
                </Box>
                <TopRightActions isAdmin={isAdmin} viewMode={viewMode} handlers={handlers} toggles={toggles} mode={mode} compactActions product={product} onSaveComments={handlers.onSaveComments} />
            </Flex>
        </Card>
    );
};

export default function Item(props) {
    const { product, handleUpdateSeller, removeFromUpdate, statusUpdate, setStatusUpdate, index, isAdmin, viewMode = "grid", abuseTypes, handleInsertRelevance, actionMode = "reported" } = props;

    // Chakra Color Mode
    const textColor = useColorModeValue("brands.900", "white");
    const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");
    const { hasCopied, onCopy } = useClipboard(product.prod_code);

    // State
    const [toggleReport, setToggleReport] = useState(false);
    const [toggleTest, setToggleTest] = useState(false);
    const [toggleNonCompliance, setToggleNonCompliance] = useState(false);
    const [toggleWhiteList, setToggleWhiteList] = useState(false);
    const [toggleReported, setToggleReported] = useState(false);
    const [toggleNonReportable, setToggleNonReportable] = useState(false);
    const [currentRelevance, setCurrentRelevance] = useState(
        product.relevance || "SELECT"
    );
    const [localComments, setLocalComments] = useState(product.comments || []);

    // Handlers (Keeping original logic)
    const handleToggleTakedown = async () => {
        setToggleReport(!toggleReport);
        setToggleTest(false);
        setToggleNonCompliance(false);
        setToggleWhiteList(false);
        setToggleReported(false);
        setToggleNonReportable(false);
        if (!toggleReport) {
            await handleUpdateSeller(product, 'TD');
        } else {
            await removeFromUpdate(product, 'TD');
        }
    };

    const handleToggleTest = async () => {
        setToggleReport(false);
        setToggleTest(!toggleTest);
        setToggleNonCompliance(false);
        setToggleWhiteList(false);
        setToggleReported(false);
        setToggleNonReportable(false);
        if (!toggleTest) {
            await handleUpdateSeller(product, 'T');
        } else {
            await removeFromUpdate(product, 'T');
        }
    };

    const handleToggleNonCompliance = async () => {
        setToggleReport(false);
        setToggleTest(false);
        setToggleNonCompliance(!toggleNonCompliance);
        setToggleWhiteList(false);
        setToggleReported(false);
        setToggleNonReportable(false);
        if (!toggleNonCompliance) {
            await handleUpdateSeller(product, 'NC');
        } else {
            await removeFromUpdate(product, 'NC');
        }
    };

    const handleToggleWhiteList = async () => {
        setToggleReport(false);
        setToggleTest(false);
        setToggleNonCompliance(false);
        setToggleWhiteList(!toggleWhiteList);
        setToggleReported(false);
        setToggleNonReportable(false);
        if (!toggleWhiteList) {
            await handleUpdateSeller(product, 'W');
        } else {
            await removeFromUpdate(product, 'W');
        }
    };

    const handleToggleReported = async () => {
        setToggleReport(false);
        setToggleTest(false);
        setToggleNonCompliance(false);
        setToggleWhiteList(false);
        setToggleReported(!toggleReported);
        setToggleNonReportable(false);
        if (!toggleReported) {
            await handleUpdateSeller(product, 'R');
        } else {
            await removeFromUpdate(product, 'R');
        }
    };

    const handleToggleNonReportable = async () => {
        setToggleReport(false);
        setToggleTest(false);
        setToggleNonCompliance(false);
        setToggleWhiteList(false);
        setToggleReported(false);
        setToggleNonReportable(!toggleNonReportable);
        if (!toggleNonReportable) {
            await handleUpdateSeller(product, 'NR');
        } else {
            await removeFromUpdate(product, 'NR');
        }
    };

    useEffect(() => {
        setToggleTest(false);
        setToggleReport(false);
        setToggleNonCompliance(false);
        setToggleWhiteList(false);
        setToggleReported(false);
        setToggleNonReportable(false);
        setStatusUpdate(false)
    }, [statusUpdate]);
    const handleRelevanceChange = (value) => {

        setCurrentRelevance(value);

        handleInsertRelevance(product, value);

    };
    useEffect(() => {
        setCurrentRelevance(product.relevance || "SELECT");
    }, [product.relevance]);

    useEffect(() => {
        setLocalComments(product.comments || []);
    }, [product.comments, product.pd_id]);
    // Common Helpers
    const commonProps = {
        textColor,
        borderColor,
        onCopy,
        hasCopied,
        getPlatformIcon: (platformId) => {
            switch (platformId) {
                case 1: return Amazon;
                case 2: return Flipkart;
                case 3: return Snapdeal;
                case 4: return Meesho;
                case 5: return Glowroad;
                case 6: return Shopsy;
                default: return null;
            }
        },
        renderStars: (rating) => {
            rating = rating < 0 ? 0 : rating;
            const filledStars = Math.floor(rating);
            const hasHalfStar = rating % 1 >= 0.5;
            const stars = [];
            for (let i = 0; i < filledStars; i++) stars.push(<Icon as={StarIcon} color="gold" key={`s-${i}`} />);
            if (hasHalfStar) stars.push(<Icon as={StarIcon} color="gold" key={`hs`} />);
            const emptyStars = 5 - stars.length;
            for (let i = 0; i < emptyStars; i++) stars.push(<Icon as={StarIcon} color="gray.300" key={`e-${i}`} />);
            return stars;
        },
        toolTipMessage: (status) => {
            if (status === 10) return "Tested and verified seller";
            if (status === 11) return "Official Seller";
            if (status === 20) return "Counterfeit products";
            if (status === 21) return "Blacklisted Seller";
            return "No info";
        },
        chooseAbuseType: () => {
            // Handle explicit "None" cases
            if (product.abuse_type_id === 0) return 'None';
            if (product.abuse_type_id === null) return 'Null';

            // Find the abuse type object in the provided array
            const found = abuseTypes?.find(type => type.id === product.abuse_type_id);
            // console.log("abuse tpes:",abuseTypes)
            console.log("found:", found)
            // Return label or fallback
            return found ? found.abuse_type : 'Choose Type';
        }

    };

    const handleSaveComments = async (comments) => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/products/updateComments`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ products: [{ pd_id: product.pd_id, comments }] }),
        });

        if (!response.ok) {
            throw new Error("Failed to save comments");
        }

        setLocalComments(comments);
    };

    const handlers = {
        onToggleReport: handleToggleTakedown,
        onToggleTest: handleToggleTest,
        onToggleNonCompliance: handleToggleNonCompliance,
        onToggleWhiteList: handleToggleWhiteList,
        onToggleReported: handleToggleReported,
        onToggleNonReportable: handleToggleNonReportable,
        onSaveComments: handleSaveComments
    };

    const toggles = {
        toggleReport,
        toggleTest,
        toggleNonCompliance,
        toggleWhiteList,
        toggleReported,
        toggleNonReportable
    };



    const cardProduct = { ...product, comments: localComments };

    return viewMode === 'grid'
        ? <GridView {...props} viewMode={viewMode} product={cardProduct} handlers={handlers} toggles={toggles} common={commonProps}
            currentRelevance={currentRelevance} handleRelevanceChange={handleRelevanceChange} mode={actionMode} />
        : <ListView {...props} viewMode={viewMode} product={cardProduct} handlers={handlers} toggles={toggles} common={commonProps}
            currentRelevance={currentRelevance} handleRelevanceChange={handleRelevanceChange} mode={actionMode} />;

}